/*
 * ServicePartnerList.tsx
 * This Service Partner list Component is used for representing all the saved partners in list Form.
 */
// Import necessary modules and components
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import GenericPopover from "../../../components/pop-over";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import { TFilterModel } from "../../../types/config";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import styles from "./partner.module.css";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { debounceTime, defaultFilterParams } from "../../../utils/constants";
import {
  Drawer,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import ServicePartnerForm from "./form";
import { TLocation } from "../../../types";
// import BulkUploadForm from "./bulk-upload/bulk-upload-form";
import CustomDrawer from "../../../components/drawer";
import { GetStateList } from "../../../utils/api.constant";
import utils from "../../../utils";
import ActionCard from "../../../components/actionCard/actionCard";
import { UserContext } from "../../../context";
import {
  CloseOutlined,
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import { log } from "console";
import ExportToCSV from "../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../utils/date";

/**
 * ServicePartnerList Component
 * This component renders the list of service partners, with
 * the ability to search, filter, and add new service partners.
 * It also includes a bulk upload feature for importing
 * service partners in bulk.
 *
 * @returns {JSX.Element} The JSX structure for the
 *         ServicePartnerList component.
 */
export default function StateConfigurationList1() {
  // State and hooks initialization
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TLocation[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<TLocation | null>(
    null
  );
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  const initialColumns: any[] = [
    {
      title: "Country",
      dataIndex: "countryName",
      key: "countryName",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "countryName"),
      sorter: (a: TLocation, b: TLocation) =>
        a.countryName.localeCompare(b.countryName),
      onFilter: (value: string | number | boolean, record: TLocation) =>
        record.countryName?.includes(value as string),
    },
    {
      title: "State",
      dataIndex: "name",
      key: "name",
      width: "30%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "name"),
      sorter: (a: TLocation, b: TLocation) => a.name.localeCompare(b.name),
      onFilter: (value: string | number | boolean, record: TLocation) =>
        record.name?.includes(value as string),
    },
    {
      title: "Aliases",
      dataIndex: "aliases",
      key: "aliases",
      width: "15%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "aliases"),
      sorter: (a: TLocation, b: TLocation) =>
        a?.aliases.localeCompare(b.aliases),
      onFilter: (value: string | number | boolean, record: TLocation) =>
        record.aliases?.includes(value as string),
    },
    {
      title: "Action",
      key: "actions",
      align: "center",
      width: "4%",
      render: (location: TLocation) => (
        <>
          <GenericPopover
            placement="left"
            content={
              <div className="standardActionBox">
                {/* Edit Row with CustomDrawer trigger */}
                {Utils.isUserHasAccess(
                  context?.user?.privilegeList,
                  ePrivileges.UPDATEALIAS
                ) ? (
                  <CustomDrawer
                    size="default"
                    trigger={
                      <ActionCard text="Edit">
                        <EditOutlined />
                      </ActionCard>
                    }
                    component={ServicePartnerForm} // Passing ServicePartner component
                    componentProps={{
                      id: location.id,
                      country: location.countryName,
                      state: location.name,
                      aliases: location.aliases,
                      stateCode: location.stateCode,
                      formType: "state", // Pass necessary props for ServicePartner
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="State Form"
                  />
                ) : null}
              </div>
            }
            icon={
              <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
            }
          />
        </>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState(
    defaultFilterParams.searchText
  );

  const context: any = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, [debouncedSearchText]);

  useEffect(() => {
    setColumns(initialColumns);
  }, [context?.user?.privilegeList]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          ...filterParams,
          countryId: 1,
          searchText: filterParams.searchText,
          pageSize: -1,
        },
      };

      const response = await post(GetStateList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TLocation, index: number) => ({
          key: (item.id ?? "").toString(),
          srNo: index + 1,
          countryName: item.countryName,
          name: item.name,
          aliases: item.aliases,
          id: item.id,
        }));
        setData(formattedData);
        setFilterParams({
          ...(filterModel as TFilterModel),
          totalRows: data.length,
        });
        setIsLoading(false);
        updateColumnFilters(filteredData);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const updateColumnFilters = (dataset: TLocation[]) => {
    const nameFilters = Array.from(
      new Set(dataset.map((item) => item.countryName))
    ).map((countryName) => ({ text: countryName, value: countryName }));

    const codeFilters = Array.from(
      new Set(dataset.map((item) => item.name))
    ).map((name) => ({ text: name, value: name }));

    const aliasesFilters = Array.from(
      new Set(dataset.map((item) => item.aliases))
    ).map((aliases) => ({ text: aliases, value: aliases }));

    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if (col.key === "countryName") {
          return { ...col, filters: nameFilters };
        }
        if (col.key === "name") {
          return { ...col, filters: codeFilters };
        }
        if (col.key === "aliases") {
          return { ...col, filters: aliasesFilters };
        }
        return col;
      })
    );
  };

  // Update search text state
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(filterParams.searchText);
    }, debounceTime); // Set debounce delay to 300 milliseconds

    return () => {
      clearTimeout(timer); // Clear the timer on each update
    };
  }, [filterParams.searchText]);

  /**
   * Updates the dealer with the given values.
   * Sends a request to the API with the given values, and if the response is successful, shows a success toast and fetches the data again.
   * If the response is not successful, shows an error toast with the description from the response.
   * @param {TDealer} values - the dealer values to update.
   */

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) => {
    const searchMatch = Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase())
    );

    if (!searchMatch) return false;

    const countryNameMatch =
      !activeFilters.countryName ||
      !activeFilters.countryName.length ||
      activeFilters.countryName.includes(item.countryName);

    const nameMatch =
      !activeFilters.name ||
      !activeFilters.name.length ||
      activeFilters.name.includes(item.name);

    const aliasesMatch =
      !activeFilters.aliases ||
      !activeFilters.aliases.length ||
      activeFilters.aliases.includes(item.aliases);

    return searchMatch && countryNameMatch && nameMatch && aliasesMatch;
  });

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action"
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };
  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total States`}
        size="large"
      >
        <Space>
          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
            prefix={<SearchOutlined />}
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`state_configuration_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            // ...pagination,
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
